import { useCallback, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import TransactionOverlay from '../../components/transactions/TransactionOverlay';
import PayBills from '../PayBills';
import type { BillPayment } from '../../types/billPayment';
import { PayBillsHubProvider } from '../PayBillsHubContext';
import { usePurchaseTransactionsHub } from './PurchaseTransactionsHubContext';
import {
  parsePurchaseHubLocationState,
  resolvePayBillOverlayCloseTarget,
} from './purchaseHubNavigation';

const BILL_OVERLAY_PATTERN = /\/payment\/pay\/bill\//;

const PurchasePaymentPayOverlay = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refetchList } = usePurchaseTransactionsHub();
  const listSearch = location.search.replace(/^\?/, '');
  const navState = useMemo(
    () => parsePurchaseHubLocationState(location.state),
    [location.state]
  );
  const billOverlayOpen = BILL_OVERLAY_PATTERN.test(location.pathname);

  const close = useCallback(() => {
    const target = resolvePayBillOverlayCloseTarget(listSearch, navState);
    navigate(target, { preventScrollReset: true });
  }, [listSearch, navState, navigate]);

  const handlePaymentSaved = useCallback(
    (_payment: BillPayment) => {
      refetchList();
      close();
    },
    [close, refetchList]
  );

  return (
    <PayBillsHubProvider>
      <TransactionOverlay headerless variant="wide" onClose={close}>
        <div
          className={`flex flex-1 min-h-0 flex-col${
            billOverlayOpen ? ' pointer-events-none select-none opacity-60' : ''
          }`}
          aria-hidden={billOverlayOpen}
        >
          <PayBills
            embedded
            initialVendorId={navState?.vendorId}
            initialBillId={navState?.billId}
            onClose={close}
            onPaymentSaved={handlePaymentSaved}
          />
        </div>
      </TransactionOverlay>
      <Outlet />
    </PayBillsHubProvider>
  );
};

export default PurchasePaymentPayOverlay;
